/**
 * Armies on the world map.
 *
 * A campaign army is a list of regiments, and a regiment is a unit type and a
 * number of models. That is deliberately the same currency battle mode spends:
 * `threat × models` is a strength point in both layers, so an army that looks
 * like 1,400 points on the map musters as 1,400 points of companies on the
 * field. Nothing has to be converted, which means nothing can be converted
 * wrongly.
 */

import { UF } from '../types.ts';
import { unitType, unitTypeIndex } from '../../data/units.ts';
import { faction } from '../../data/factions.ts';
import { nationDef } from '../../data/nations.ts';
import { provinceDef } from '../../data/provinces.ts';
import { nextInt, type Rng } from '../../core/rng.ts';
import { areAdjacent, canWalkBetween, linkBetween, neighbours } from './graph.ts';
import { isWithdrawalStep, mayEnter } from './diplopass.ts';
import { mayAttack } from './state.ts';
import {
  ArmyStance,
  MAX_ARMIES_PER_NATION,
  MAX_ARMY_POINTS,
  MAX_VETERANCY,
  MIN_ARMY_POINTS,
  RAISE_GOLD_PER_POINT,
  RAISE_IRON_PER_POINT,
  VETERANCY_PER_WIN,
  type Army,
  type CampaignState,
  type Regiment,
} from './types.ts';

/** Raw points before veterancy. */
export function regimentPoints(r: Regiment): number {
  return unitType(r.typeIdx).threat * r.models;
}

export function rawPoints(a: Army): number {
  let total = 0;
  for (const r of a.regiments) total += regimentPoints(r);
  return total;
}

/** What the army is actually worth in a fight: size, experience and supply. */
export function armyPoints(a: Army): number {
  const vet = 1 + Math.min(MAX_VETERANCY, a.victories * VETERANCY_PER_WIN);
  // Starving men fight, but not well, and this is the only lever hunger has.
  const fed = 0.6 + 0.4 * Math.max(0, Math.min(1, a.supply));
  return rawPoints(a) * vet * fed;
}

export function armyModels(a: Army): number {
  let n = 0;
  for (const r of a.regiments) n += r.models;
  return n;
}

export function armyAlive(a: Army): boolean {
  return a.regiments.length > 0 && armyModels(a) > 0;
}

export function findArmy(c: CampaignState, id: number): Army | undefined {
  return c.armies.find((a) => a.id === id);
}

/**
 * Every army whose `at` is this province, including ones currently on a road
 * out of it.
 *
 * Kept with its original meaning because the world map lists an army under the
 * province it set out from and prints its destination beside it. When you mean
 * "who is actually here to fight" — which is what the AI's threat arithmetic and
 * every contact check mean — use `armiesStandingIn`.
 */
export function armiesIn(c: CampaignState, province: number): Army[] {
  return c.armies.filter((a) => a.at === province && a.aboard < 0);
}

/** A host aboard a ship. Its `at` follows the hull and means nothing on land. */
export function isEmbarked(a: Army): boolean {
  return a.aboard >= 0;
}

/**
 * Armies genuinely standing in a province: not marching, and therefore able to
 * defend it, contest it, or be caught in it.
 *
 * The distinction only became real when marches did. Before, a host on a road
 * still counted as holding the ground it had left, so a province could be
 * "defended" by an army three turns away — which made the AI's threat model
 * wrong and would have made the warning list lie.
 */
export function armiesStandingIn(c: CampaignState, province: number): Army[] {
  return c.armies.filter((a) => a.at === province && a.moveTo < 0 && a.aboard < 0);
}

export function armiesOf(c: CampaignState, nation: number): Army[] {
  return c.armies.filter((a) => a.owner === nation);
}

/**
 * Spend a points budget on a sane mix from a faction's roster.
 *
 * The shares are the same four roles battle mode musters to — a line to hold,
 * shot to soften, a flanking arm, something heavy — so an army raised on the
 * map deploys as an army rather than as a pile.
 */
export function buildRegiments(rng: Rng, factionId: string, points: number): Regiment[] {
  const fac = faction(factionId);
  const roster = fac.roster.map((id) => unitTypeIndex(id)).filter((i) => i >= 0);
  if (roster.length === 0) return [];

  const line = roster.filter((i) => isLine(i));
  const shot = roster.filter((i) => (unitType(i).flags & UF.Ranged) !== 0);
  const horse = roster.filter((i) => (unitType(i).flags & UF.Cavalry) !== 0);
  const heavy = roster.filter(
    (i) => (unitType(i).flags & (UF.Large | UF.Siege | UF.Champion | UF.Caster)) !== 0,
  );

  const plan: Array<{ pool: number[]; share: number }> = [
    { pool: line.length ? line : roster, share: 0.44 },
    { pool: shot.length ? shot : roster, share: 0.24 },
    { pool: horse.length ? horse : line.length ? line : roster, share: 0.17 },
    { pool: heavy.length ? heavy : line.length ? line : roster, share: 0.15 },
  ];

  const out: Regiment[] = [];
  /**
   * `points` is a ceiling, not a target. It has to be, because the caller pays
   * for what comes out of here: a muster that overshot its budget by five per
   * cent used to be refused outright at the till, so "raise the largest host
   * you can afford" reliably raised nothing at all.
   *
   * The ceiling is enforced by **picking from what the purse can still afford**
   * rather than by picking freely and rejecting. Rejection looked equivalent and
   * was not: an unaffordable draw burned a `guard` tick, so a role facing a
   * roster of mostly-too-dear companies would exhaust its twelve tries and
   * contribute nothing, and the *first* draw of the whole muster was taken
   * unconditionally on the "an army of one beats an army of none" rule — which
   * was written when the dearest company on any roster was a hundred and eighty
   * points and the budget floor was two hundred.
   *
   * Both assumptions died with the upper Ages. A company of twelve Hollowed is
   * twelve hundred points, so a four-hundred-point muster was reliably drawing
   * one, taking it unconditionally, and delivering three times the army the
   * nation had paid for. Filtering first cannot do that: nothing is ever drawn
   * that there is no room for, and the fallback below is reached only when the
   * cheapest company in the whole roster will not fit.
   */
  let spent = 0;
  for (const { pool, share } of plan) {
    const target = points * share;
    let roleSpent = 0;
    let guard = 0;
    while (roleSpent < target && guard++ < 12) {
      const room = points - spent;
      const afford = pool.filter((i) => {
        const cost = companyCost(i);
        return cost > 0 && cost <= room;
      });
      // Nothing in this arm fits what is left. Abandon the role rather than the
      // muster — an army with no cavalry is an army; an army of one dearest
      // knight and nothing else is a mistake.
      if (afford.length === 0) break;
      const idx = afford[nextInt(rng, 0, afford.length - 1)];
      const t = unitType(idx);
      const cost = companyCost(idx);
      addRegiment(out, idx, t.companySize);
      spent += cost;
      roleSpent += cost;
    }
  }
  if (out.length === 0) {
    // The budget will not cover one company of anything, which a floor of
    // `MIN_ARMY_POINTS` makes rare and a roster of engines makes possible. Take
    // the cheapest thing in the roster and overshoot knowingly: this is the one
    // place the ceiling gives, and it gives by as little as the roster allows.
    let best = roster[0];
    for (const i of roster) if (companyCost(i) < companyCost(best)) best = i;
    addRegiment(out, best, unitType(best).companySize);
  }
  return out;
}

/** Points for one full company of a type — the unit the muster actually buys in. */
function companyCost(typeIdx: number): number {
  const t = unitType(typeIdx);
  return t.threat * t.companySize;
}

function isLine(i: number): boolean {
  const t = unitType(i);
  return (t.flags & (UF.Ranged | UF.Cavalry | UF.Large | UF.Siege | UF.Champion | UF.Caster)) === 0;
}

/** Regiments of the same type merge, so a big army is a short list. */
export function addRegiment(list: Regiment[], typeIdx: number, models: number): void {
  if (models <= 0) return;
  const existing = list.find((r) => r.typeIdx === typeIdx);
  if (existing) existing.models += models;
  else list.push({ typeIdx, models });
}

/**
 * Take casualties. `survive` is 0..1 and is applied to every regiment, which is
 * a simplification the campaign layer is entitled to: the *tactical* layer is
 * where which company died is interesting.
 */
export function applyLosses(a: Army, survive: number): number {
  const before = armyModels(a);
  const keep = Math.max(0, Math.min(1, survive));
  for (const r of a.regiments) r.models = Math.floor(r.models * keep);
  a.regiments = a.regiments.filter((r) => r.models > 0);
  return before - armyModels(a);
}

export function makeArmy(
  c: CampaignState,
  owner: number,
  at: number,
  regiments: Regiment[],
): Army {
  const a: Army = {
    id: c.nextArmyId++,
    owner,
    at,
    moveTo: -1,
    moveT: 0,
    moveFrom: -1,
    moveTotal: 0,
    dest: -1,
    intercepting: -1,
    regroup: 0,
    regiments,
    stance: ArmyStance.Field,
    siegeTurns: 0,
    supply: 1,
    victories: 0,
    objective: -1,
    idleTurns: 0,
    aboard: -1,
    trespassT: 0,
    // Every host is raised in the open. Getting inside is an order.
    sheltered: false,
    name: hostName(c, owner, at),
  };
  c.armies.push(a);
  return a;
}

/** Armies are named for where they were raised, and counted per nation. */
function hostName(c: CampaignState, owner: number, at: number): string {
  const n = c.armies.filter((a) => a.owner === owner).length + 1;
  return `${ordinal(n)} Host of ${provinceDef(at).name}`;
}

const ORDINALS = [
  'First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Seventh', 'Eighth',
  'Ninth', 'Tenth', 'Eleventh', 'Twelfth',
];

function ordinal(n: number): string {
  return ORDINALS[n - 1] ?? `${n}th`;
}

/** Fold `from` into `into`. Both must belong to the same nation and stand together. */
export function mergeArmies(c: CampaignState, into: Army, from: Army): boolean {
  if (into.id === from.id) return false;
  if (into.owner !== from.owner) return false;
  if (into.at !== from.at) return false;
  if (into.moveTo >= 0 || from.moveTo >= 0) return false;
  // Two hosts cannot be folded together across a gunwale.
  if (into.aboard >= 0 || from.aboard >= 0) return false;
  for (const r of from.regiments) addRegiment(into.regiments, r.typeIdx, r.models);
  into.victories = Math.max(into.victories, from.victories);
  into.supply = Math.min(into.supply, from.supply);
  from.regiments = [];
  removeArmy(c, from.id);
  return true;
}

export function removeArmy(c: CampaignState, id: number): void {
  const i = c.armies.findIndex((a) => a.id === id);
  if (i >= 0) c.armies.splice(i, 1);
}

/** A one-line composition summary for the world-map panel. */
export function armySummary(a: Army): string {
  return a.regiments
    .slice()
    .sort((x, y) => y.models - x.models)
    .map((r) => `${r.models} ${unitType(r.typeIdx).name}`)
    .join(', ');
}

export function nationFactionId(c: CampaignState, nation: number): string {
  const n = c.nations[nation];
  return n ? nationDef(n.def).factionId : 'ashen';
}

// ── Marching ───────────────────────────────────────────────────────────────

/**
 * Why this army may not march to `to`, as a number. 0 means it may.
 *
 * The clauses live here once and nowhere else. `canMarch` asks whether this is
 * zero and `marchRefusal` turns it into a sentence, so the rule the UI greys a
 * button with and the rule the simulation enforces are the same rule by
 * construction rather than by two people remembering to edit both.
 *
 * A code rather than the string itself because `canMarch` is hot — the AI asks
 * it thousands of times a turn, mostly about provinces it cannot reach — and
 * naming a crown to build a sentence nobody reads is work on the wrong path.
 */
const MarchProblem = {
  None: 0,
  Marching: 1,
  Aboard: 2,
  NotAProvince: 3,
  NoRoad: 4,
  /** Their ground, and no arrangement that lets you onto it. */
  NoPassage: 5,
  /** Beaten on the road and not yet in a state to be given a new one. */
  Regrouping: 6,
  /**
   * Inside the walls. It has to be brought out before it can be sent anywhere.
   *
   * A clause here rather than a check at the two or three call sites, so the
   * button the UI greys and the order the simulation refuses come out of the
   * same list — and so `marchPath`, which asks `canMarch` about the first leg,
   * refuses a sheltered host without knowing what sheltering is.
   */
  Sheltered: 7,
} as const;

function marchProblem(c: CampaignState, a: Army, to: number): number {
  if (a.moveTo >= 0) return MarchProblem.Marching;
  /**
   * A host that has just been beaten on a road is not marching anywhere.
   *
   * This is the clause that closes the running fight, and it belongs here rather
   * than at the two or three places an order is issued precisely because the AI
   * asks `canMarch` thousands of times a month and the player asks
   * `canOrderMarch` once — one rule, both callers, no way for the council to
   * send a host back down a road the player could not have been sent down.
   */
  if (a.regroup > 0) return MarchProblem.Regrouping;
  // Read straight off the flag rather than through `garrisons.ts`, on purpose:
  // this is the hottest predicate in the layer, and it keeps the import arrow
  // between the two files pointing one way.
  if (a.sheltered) return MarchProblem.Sheltered;
  // A host in a transport does not take roads. It goes where the hull goes.
  if (a.aboard >= 0) return MarchProblem.Aboard;
  if (to === a.at || to < 0 || to >= c.provinces.length) return MarchProblem.NotAProvince;
  if (!neighbours(c, a.at).includes(to)) return MarchProblem.NoRoad;
  /**
   * Whose ground it is, which is now a treaty question rather than a war
   * question. The whole rule is `mayEnter`, in `diplopass.ts`, and this is the
   * only place the land half of it is enforced.
   *
   * The second clause is the one that stops closed borders being a trap. A host
   * that has no right to stand where it *already* stands — a peace was signed
   * under it, a grant was taken back, the war it shared with the crown that let
   * it in is over — may always walk toward ground where it has one, including
   * straight across the country that has just shut its border. Without it a
   * crown could seal a host three provinces deep inside itself, every neighbour
   * of every province on the way being its own, and the host would have no legal
   * order at all. It would stand there and starve, having been given no move
   * that could have saved it.
   */
  if (!mayEnter(c, a.owner, to)) {
    if (isWithdrawalStep(c, a.owner, a.at, to)) return MarchProblem.None;
    return MarchProblem.NoPassage;
  }
  return MarchProblem.None;
}

/** Whether this army may legally be ordered from where it stands to `to`. */
export function canMarch(c: CampaignState, a: Army, to: number): boolean {
  return marchProblem(c, a, to) === MarchProblem.None;
}

/**
 * The same answer as a sentence to show the player, or `''` when the march is
 * legal. The convention `fleets.ts` uses for every naval verb.
 *
 * `NoRoad` is the one worth reading twice. On this board it is the *ordinary*
 * answer, not an edge case: the road graph is in seven components and 111 of
 * 150 provinces cannot be reached from the heartland on foot at all. So the
 * sentence says what to do about it rather than only what is wrong, because
 * "there is no road" with no mention of ships is a dead end to a player who has
 * not yet worked out that most of the world needs a fleet.
 */
export function marchRefusal(c: CampaignState, a: Army, to: number): string {
  switch (marchProblem(c, a, to)) {
    case MarchProblem.Marching:
      return 'This host is already on the road.';
    case MarchProblem.Aboard:
      return 'This host is aboard ship. It goes where the fleet goes.';
    case MarchProblem.Sheltered:
      return 'This host is inside the walls. Bring it out before it marches.';
    case MarchProblem.NotAProvince:
      return 'Nowhere to march to.';
    case MarchProblem.NoRoad:
      return canWalkBetween(c, a.at, to)
        ? 'No road runs straight there. March by way of the provinces between.'
        : 'No road reaches there from here. It must be taken by sea.';
    case MarchProblem.Regrouping:
      return 'This host was thrown off the road and is still forming up.';
    case MarchProblem.NoPassage: {
      const owner = c.provinces[to].owner;
      const crown = c.nations[owner];
      const name = crown ? nationDef(crown.def).name : 'its holder';
      // Names the way out as well as the wall. A refusal that only says "no" is
      // a dead end; this one says which door to knock on, and there is a
      // command behind it.
      return `${name} has not granted you passage. Ask for it, or declare on them.`;
    }
    default:
      return '';
  }
}

/**
 * Whether this host may be *ordered* to `to` — the destination rule, as
 * against `canMarch`'s single-leg rule.
 *
 * The two are deliberately separate functions rather than one that got
 * cleverer. `canMarch` is asked thousands of times a turn by the AI, mostly
 * about provinces it cannot reach, and it answers from an adjacency list; this
 * one walks a breadth-first search and belongs on the player's path, where it
 * is asked once per province under the cursor. Making the hot one do the
 * expensive thing to save a name would be the wrong trade twice over.
 */
export function canOrderMarch(c: CampaignState, a: Army, to: number): boolean {
  if (a.moveTo >= 0 || a.aboard >= 0 || a.regroup > 0 || a.sheltered) return false;
  const path = marchPath(c, a, to);
  return path.length > 0 && canMarch(c, a, path[0]);
}

/**
 * Why this host may not be ordered to `to`, as a sentence, or `''`.
 *
 * Falls through to `marchRefusal` for a neighbouring province so the familiar
 * answers — closed borders, already marching, aboard ship — are still the
 * simulation's own words rather than a second set that can drift from them.
 */
export function orderRefusal(c: CampaignState, a: Army, to: number): string {
  if (a.moveTo >= 0) return 'This host is already on the road.';
  if (a.regroup > 0) return 'This host was thrown off the road and is still forming up.';
  if (a.sheltered) return 'This host is inside the walls. Bring it out before it marches.';
  if (a.aboard >= 0) return 'This host is aboard ship. It goes where the fleet goes.';
  if (to === a.at || to < 0 || to >= c.provinces.length) return 'Nowhere to march to.';
  if (areAdjacent(c, a.at, to)) return marchRefusal(c, a, to);
  const path = marchPath(c, a, to);
  if (path.length === 0) {
    return canWalkBetween(c, a.at, to)
      ? 'No road is open to them all the way there. Ask for passage, or take another way.'
      : 'No road reaches there from here. It must be taken by sea.';
  }
  return canMarch(c, a, path[0]) ? '' : marchRefusal(c, a, path[0]);
}

/**
 * Months the whole journey would take, or 0 if it is not a legal order.
 *
 * The *whole* journey, summed over every leg — because that is the number a
 * player is deciding on. Quoting the first leg of an eight-province march would
 * be quoting a number that is true and useless.
 */
export function marchTurns(c: CampaignState, a: Army, to: number): number {
  const path = marchPath(c, a, to);
  if (path.length === 0 || !canMarch(c, a, path[0])) return 0;
  let total = 0;
  let from = a.at;
  for (const step of path) {
    const link = linkBetween(c, from, step);
    if (!link) return 0;
    total += link.march;
    from = step;
  }
  return total;
}

/**
 * The provinces this host would walk through to reach `to`, `at` excluded.
 *
 * Breadth-first over the same link graph a single hop uses, and gated by the
 * same `mayEnter` — so a route cannot be a way of walking through a closed
 * border that a one-hop order would have refused. Hops rather than months on
 * purpose: fewest borders crossed is what a host marching across a continent
 * actually wants, and pricing the search in months would send it the long way
 * round through friendly flatland often enough to look broken.
 *
 * Empty when there is no legal walking route at all, which is the honest answer
 * on this board — the road graph is in seven components and most of the world
 * needs a fleet.
 */
export function marchPath(c: CampaignState, a: Army, to: number): number[] {
  if (to === a.at || to < 0 || to >= c.provinces.length) return [];
  const prev = new Map<number, number>();
  const queue = [a.at];
  prev.set(a.at, -1);
  for (let head = 0; head < queue.length; head++) {
    const at = queue[head];
    if (at === to) break;
    for (const n of neighbours(c, at)) {
      if (prev.has(n)) continue;
      /**
       * The first step keeps the withdrawal escape the one-hop rule has: a host
       * standing where it has no right to stand may always walk toward ground
       * where it has one. Later steps do not, because a route is a plan and a
       * plan may not assume a border will still be shut when it gets there.
       */
      if (!mayEnter(c, a.owner, n) && !(at === a.at && isWithdrawalStep(c, a.owner, a.at, n))) continue;
      prev.set(n, at);
      queue.push(n);
    }
  }
  if (!prev.has(to)) return [];
  const path: number[] = [];
  for (let p = to; p >= 0 && p !== a.at; p = prev.get(p) ?? -1) path.unshift(p);
  return path;
}

/**
 * Send a host to a province — any province it can walk to, not merely a
 * neighbouring one.
 *
 * The order names the destination and the simulation walks the legs; see
 * `Army.dest`. A neighbouring province is the one-leg case of the same rule
 * rather than a separate path through the code, which is what keeps
 * interception, halting and the drawn lane working exactly as they did.
 */
export function orderMarch(c: CampaignState, a: Army, to: number): boolean {
  if (a.moveTo >= 0 || a.aboard >= 0 || a.regroup > 0 || a.sheltered) return false;
  const path = marchPath(c, a, to);
  if (path.length === 0) return false;
  const first = path[0];
  // The first leg still has to be a legal march in its own right — this is what
  // keeps `mayEnter`, the withdrawal case and "already marching" in one place.
  if (!canMarch(c, a, first)) return false;
  a.dest = to;
  return beginLeg(c, a, first);
}

/** Set one leg of a march going. The whole of what a march *is*, once. */
function beginLeg(c: CampaignState, a: Army, to: number): boolean {
  const link = linkBetween(c, a.at, to);
  const turns = link ? link.march : 0;
  if (turns <= 0) return false;
  a.moveFrom = a.at;
  a.moveTo = to;
  a.moveT = turns;
  a.moveTotal = turns;
  // A host that sets out stops chasing whatever it was chasing. Both are orders
  // for the coming turn and a host has one turn.
  a.intercepting = -1;
  a.stance = ArmyStance.Field;
  a.siegeTurns = 0;
  return true;
}

/**
 * A leg has landed. Take the next one, or stand down.
 *
 * Called by `doMarch` the moment an army arrives. Re-checking the route rather
 * than trusting the one computed when the order was given is deliberate: months
 * pass on a leg, and a border that was open when the host set out may be shut
 * by the time it gets there. A host whose road has closed under it stops where
 * it is, which is the same thing that happens to one that is refused a one-hop
 * order, and for the same reason.
 */
export function continueMarch(c: CampaignState, a: Army): void {
  if (a.dest < 0) return;
  if (a.dest === a.at) {
    a.dest = -1;
    return;
  }
  const path = marchPath(c, a, a.dest);
  if (path.length === 0 || !canMarch(c, a, path[0]) || !beginLeg(c, a, path[0])) a.dest = -1;
}

/**
 * Sit down in front of the walls, or get back up.
 *
 * The gate is `mayAttack` rather than "not my own province", and the difference
 * only became real when passage did: a host standing on a neutral's ground —
 * left there by a peace, or walked in under a grant — could otherwise be ordered
 * to besiege the crown that had let it in, and `doSieges` would grind the
 * garrison down and take the province without a war ever being declared. Owning
 * the ground is still refused; `mayAttack` refuses that case too.
 */
export function setSiegeStance(c: CampaignState, a: Army, stance: number): boolean {
  if (a.moveTo >= 0 || a.aboard >= 0) return false;
  // A host inside its own walls is not besieging anybody, and the province it
  // would be besieging is its own — `mayAttack` refuses that anyway. Said here
  // so the refusal is about the shelter rather than about the ownership.
  if (a.sheltered) return false;
  if (stance !== ArmyStance.Field && !mayAttack(c, a.owner, a.at)) return false;
  if (a.stance === stance) return false;
  a.stance = stance;
  if (stance === ArmyStance.Field) a.siegeTurns = 0;
  return true;
}

// ── Raising ────────────────────────────────────────────────────────────────

export function raiseCost(points: number): { gold: number; iron: number } {
  return {
    gold: Math.round(points * RAISE_GOLD_PER_POINT),
    iron: Math.round(points * RAISE_IRON_PER_POINT),
  };
}

/**
 * Arms come from your own forges or from somebody else's, and somebody else's
 * cost more.
 *
 * Iron is *not* a gate. The first version of this made it one, and the result
 * was that the Litany — which holds three provinces and not one seam of iron —
 * mustered twice in a hundred and twenty turns and then sat on four thousand
 * gold forever. A strategic resource that can eliminate a nation from the game
 * by arithmetic is not a strategic resource, it is a bug with a theme.
 */
const GOLD_PER_MISSING_IRON = 7;

/** Pay for a muster or a reinforcement; returns false and spends nothing if it cannot. */
function payFor(
  c: CampaignState,
  nation: number,
  points: number,
  models: number,
): boolean {
  const n = c.nations[nation];
  const cost = raiseCost(points);
  const ironPaid = Math.min(n.treasury.iron, cost.iron);
  const goldNeeded = cost.gold + (cost.iron - ironPaid) * GOLD_PER_MISSING_IRON;
  if (n.treasury.gold < goldNeeded) return false;
  if (n.manpower < models) return false;
  n.treasury.gold -= goldNeeded;
  n.treasury.iron -= ironPaid;
  n.manpower -= models;
  return true;
}

/** The bill for a muster of this size, after the iron-for-gold substitution. */
export function raisePrice(
  c: CampaignState,
  nation: number,
  points: number,
): { gold: number; iron: number } {
  const cost = raiseCost(points);
  const have = c.nations[nation]?.treasury.iron ?? 0;
  const iron = Math.min(have, cost.iron);
  return { gold: cost.gold + (cost.iron - iron) * GOLD_PER_MISSING_IRON, iron };
}

/**
 * The largest host this nation could actually raise right now.
 *
 * Exists so the world map can put a real number on the button instead of a
 * nominal one. A button that quotes a price it does not charge and then does
 * nothing when clicked is worse than no button.
 */
export function maxAffordablePoints(c: CampaignState, nation: number): number {
  const n = c.nations[nation];
  if (!n) return 0;
  /**
   * Keep back what the rounding in `raiseCost` can cost.
   *
   * The bill is rounded twice — gold, and iron before iron is bought in at
   * `GOLD_PER_MISSING_IRON` — so the price actually charged can exceed the
   * price this function solves for by half a unit of each. Without the
   * headroom, this quotes a number that is occasionally one gold too dear, the
   * till refuses it, and "raise the largest host you can afford" raises
   * nothing. That is the exact failure the iron-for-gold substitution was
   * written to avoid, reintroduced by arithmetic.
   */
  const headroom = 0.5 + 0.5 * GOLD_PER_MISSING_IRON;
  const gold = Math.max(0, n.treasury.gold - headroom);
  const iron = n.treasury.iron;
  // Below the iron ceiling the bill is flat gold; above it, every missing iron
  // is bought in at a premium, so the two branches have different slopes.
  const covered = gold / RAISE_GOLD_PER_POINT;
  const ironCeiling = iron / RAISE_IRON_PER_POINT;
  let byPurse: number;
  if (covered <= ironCeiling) {
    byPurse = covered;
  } else {
    const slope = RAISE_GOLD_PER_POINT + RAISE_IRON_PER_POINT * GOLD_PER_MISSING_IRON;
    byPurse = (gold + iron * GOLD_PER_MISSING_IRON) / slope;
  }
  const byLevies = n.manpower * meanThreat(nationFactionId(c, nation));
  return Math.max(0, Math.floor(Math.min(byPurse, byLevies, MAX_ARMY_POINTS)));
}

/**
 * Average points per model across a faction's roster; how levies become points.
 *
 * Exported so the levy screen can price a muster in *men* off the same constant
 * `maxAffordablePoints` solves the bank with. A second copy of this ratio in
 * the UI would be a screen that quotes a levy bill the till does not charge.
 */
export function meanThreat(factionId: string): number {
  const roster = faction(factionId).roster.map((id) => unitTypeIndex(id)).filter((i) => i >= 0);
  if (roster.length === 0) return 10;
  let sum = 0;
  for (const i of roster) sum += unitType(i).threat;
  return sum / roster.length;
}

function totalModels(regiments: Regiment[]): number {
  let n = 0;
  for (const r of regiments) n += r.models;
  return n;
}

/**
 * Muster a host. Returns null and spends nothing when it cannot be afforded, so
 * both the player's command and the AI's council use exactly the same rules —
 * an AI that can cheat its own economy is not an opponent, it is weather.
 */
export function tryRaiseArmy(
  c: CampaignState,
  rng: Rng,
  nation: number,
  province: number,
  wantPoints: number,
): Army | null {
  const n = c.nations[nation];
  if (!n || !n.alive) return null;
  if (province < 0 || province >= c.provinces.length) return null;
  if (c.provinces[province].owner !== nation) return null;
  if (c.provinces[province].siegeBy >= 0) return null;
  if (armiesOf(c, nation).length >= MAX_ARMIES_PER_NATION) return null;

  const points = Math.max(MIN_ARMY_POINTS, Math.min(MAX_ARMY_POINTS, Math.round(wantPoints)));
  const regiments = buildRegiments(rng, nationFactionId(c, nation), points);
  if (regiments.length === 0) return null;
  // Pay for what was actually mustered, never for the budget. A roster whose
  // company sizes do not divide the budget neatly used to charge the full
  // asking price and deliver a fifth of an army.
  const built = regiments.reduce((n, r) => n + regimentPoints(r), 0);
  if (!payFor(c, nation, built, totalModels(regiments))) return null;
  return makeArmy(c, nation, province, regiments);
}

/**
 * Top a battered host back up where it stands, which has to be ground its own
 * nation holds. This is what a nation does with money once it has as many
 * armies as it is allowed — without it, the treasury is a scoreboard.
 */
export function tryReinforceArmy(
  c: CampaignState,
  rng: Rng,
  nation: number,
  armyId: number,
  wantPoints: number,
): number {
  const n = c.nations[nation];
  const a = findArmy(c, armyId);
  if (!n || !n.alive || !a || a.owner !== nation) return 0;
  // Not on the road, and not on the water: fresh levies join a host that is
  // standing on its own ground, and a transport is neither.
  if (a.moveTo >= 0 || a.aboard >= 0) return 0;
  if (c.provinces[a.at].owner !== nation) return 0;

  const room = MAX_ARMY_POINTS - rawPoints(a);
  const points = Math.min(Math.round(wantPoints), Math.floor(room));
  if (points < MIN_ARMY_POINTS * 0.4) return 0;

  const fresh = buildRegiments(rng, nationFactionId(c, nation), points);
  if (fresh.length === 0) return 0;
  const models = totalModels(fresh);
  const built = fresh.reduce((n, r) => n + regimentPoints(r), 0);
  if (!payFor(c, nation, built, models)) return 0;

  for (const r of fresh) addRegiment(a.regiments, r.typeIdx, r.models);
  // New men do not inherit the veterans' campaign, and they arrive fed.
  a.supply = Math.min(1, a.supply + 0.4);
  return models;
}
